import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PreparationModalRef, SelectedProductData } from '../components/modals';
import { PendingOrderWithIds } from './usePendingOrders';

// 🎯 Hook dedicado únicamente a la gestión de selección de productos
export const useProductSelection = (processedOrders: PendingOrderWithIds[]) => {
    // Estado para la selección de productos
    const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());

    // Referencia al modal de preparación
    const preparationModalRef = useRef<PreparationModalRef>(null);

    // 🎯 Memorizar las claves de productos para evitar recálculos constantes
    // Usar una clave más estable que no incluya el index
    const productKeys = useMemo(() =>
        processedOrders.map(order =>
            `${order.order_id}-${order.product.id}`
        ), [processedOrders]);

    // 🎯 Referencia a productKeys para usar en useEffect sin dependencia circular
    const productKeysRef = useRef<string[]>([]);
    productKeysRef.current = productKeys;

    // 🎯 Memorizar el total de productos
    const totalProducts = useMemo(() => processedOrders.length, [processedOrders.length]);

    // 🎯 Limpiar selecciones cuando cambia sustancialmente la lista
    useEffect(() => {
        setSelectedProducts(prev => {
            // Solo limpiar si no hay productos
            if (totalProducts === 0) {
                return new Set();
            }

            // Si tenemos productos pero no hay selección previa, mantener vacío
            if (prev.size === 0) {
                return prev;
            }

            // Validar que las claves seleccionadas aún existan usando la ref
            const validKeys = new Set(productKeysRef.current);
            const filteredSelection = new Set([...prev].filter(key => validKeys.has(key)));

            // Solo actualizar si realmente cambió algo
            return filteredSelection.size !== prev.size ? filteredSelection : prev;
        });
    }, [totalProducts]); // Solo depender de totalProducts para evitar bucles

    // 🎯 Memorizar valores booleanos para evitar recálculos
    const selectionStats = useMemo(() => ({
        areAllProductsSelected: totalProducts > 0 && selectedProducts.size === totalProducts,
        areSomeProductsSelected: selectedProducts.size > 0
    }), [selectedProducts.size, totalProducts]);

    // Funciones para manejar selección de productos (estables)
    const toggleProductSelection = useCallback((productKey: string) => {
        setSelectedProducts(prev => {
            const newSet = new Set(prev);
            if (newSet.has(productKey)) {
                newSet.delete(productKey);
            } else {
                newSet.add(productKey);
            }
            return newSet;
        });
    }, []);

    const selectAllProducts = useCallback(() => {
        setSelectedProducts(new Set(productKeys));
    }, [productKeys]);

    const deselectAllProducts = useCallback(() => {
        setSelectedProducts(new Set());
    }, []);

    const toggleSelectAll = useCallback(() => {
        if (selectedProducts.size === totalProducts) {
            deselectAllProducts();
        } else {
            selectAllProducts();
        }
    }, [selectedProducts.size, totalProducts, deselectAllProducts, selectAllProducts]);

    // 🎯 Memorizar datos de productos seleccionados para evitar recálculos
    const selectedOrdersData = useMemo((): SelectedProductData[] => {
        if (selectedProducts.size === 0) return [];

        return processedOrders
            .filter(order => {
                const productKey = `${order.order_id}-${order.product.id}`;
                return selectedProducts.has(productKey);
            })
            .map(order => ({
                productId: order.product.id,
                productName: order.product.name,
                productCode: order.product.code,
                productImage: order.product.image_hash,
                quantity: order.qty,
                orderIds: order.order_ids
            }));
    }, [selectedProducts, processedOrders]);

    // 🎯 Función para abrir modal de preparación (estable)
    const openPreparationModal = useCallback(() => {
        if (selectedOrdersData.length === 0) return;

        // Abrir modal con los datos memorizados
        preparationModalRef.current?.openWithProducts({
            selectedProducts: selectedOrdersData,
            totalProducts: selectedProducts.size
        });
    }, [selectedOrdersData, selectedProducts.size]);

    // 🎯 Función para manejar confirmación de preparación (estable)
    const handlePreparationConfirmed = useCallback(() => {
        // Limpiar selección después de confirmar
        deselectAllProducts();
    }, [deselectAllProducts]);

    return {
        // Estado de selección
        selectedProducts,
        areAllProductsSelected: selectionStats.areAllProductsSelected,
        areSomeProductsSelected: selectionStats.areSomeProductsSelected,

        // Funciones de selección (todas estables)
        toggleProductSelection,
        selectAllProducts,
        deselectAllProducts,
        toggleSelectAll,

        // Modal de preparación
        preparationModalRef,
        openPreparationModal,
        handlePreparationConfirmed,

        // Mantener compatibilidad (deprecated)
        sendSelectedToPreparation: openPreparationModal,
    };
}; 